import { createContext, useContext, useEffect, useRef, useState } from "react";
import { http, formatApiErrorDetail } from "../lib/api";
import { prefetch, invalidateCache } from "../lib/swr";

const AuthCtx = createContext(null);

// Reference data nearly every page needs — warmed into the SWR cache on login
// so dropdowns/labels are instant everywhere.
const REF_PREFETCH = [
  ["currencies:all", "/currencies"],
  ["currencies:active", "/currencies?active_only=true"],
  ["bank-accounts:all", "/bank-accounts"],
  ["bank-accounts:active", "/bank-accounts?active_only=true"],
  ["coa:all", "/coa"],
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, object=user
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/auth/me");
        setUser(data);
        prefetch(REF_PREFETCH);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // "Linked device" session sync: if this account is logged out on any other
  // device (which bumps token_version server-side), our token becomes invalid
  // and /auth/me returns 401 → sign out here too.
  useEffect(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (!user || !user.id) return;
    pollRef.current = setInterval(async () => {
      try { await http.get("/auth/me"); }
      catch (e) {
        if (e.response?.status === 401) {
          localStorage.removeItem("token");
          setUser(false);
        }
      }
    }, 30000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user]);

  const finalizeSession = (data) => {
    if (data.token) localStorage.setItem("token", data.token);
    setUser(data);
    prefetch(REF_PREFETCH);
  };

  const login = async (email, password, captcha) => {
    try {
      const payload = { email, password };
      if (captcha) { payload.captcha_id = captcha.id; payload.captcha_text = captcha.text; }
      const { data } = await http.post("/auth/login", payload);
      // 2FA gate: only create a session once the server returns "authenticated".
      // For 2fa_setup_required / 2fa_required we just hand the pending token back
      // to the Login page and DO NOT set the user yet.
      if (data.status === "authenticated" || (data.token && !data.status)) {
        finalizeSession(data);
      }
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await http.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("token");
    invalidateCache();   // drop all cached data so the next user starts clean
    setUser(false);
  };

  const refreshUser = async () => {
    try { const { data } = await http.get("/auth/me"); setUser(data); } catch { /* keep current */ }
  };

  // Sensitive-action permission check. super_admin always allowed.
  const can = (perm) => {
    if (!user) return false;
    if (user.role === "super_admin") return true;
    return !!(user.permissions && user.permissions[perm]);
  };

  return <AuthCtx.Provider value={{ user, loading, login, logout, refreshUser, can, finalizeSession }}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
